import { useEffect, useState } from "react";
import { cn } from "@rekati/utils";
import { useI18n } from "../../content/i18n";

function parts(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

function useNow(intervalMs: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * Days, hours, minutes and seconds to the doors. The ticking figures are
 * decoration; assistive tech hears one sentence ("21 days to go"), not a
 * number every second.
 */
export function Countdown({ to, className }: { to: string; className?: string }) {
  const { t } = useI18n();
  const now = useNow(1000);
  const left = new Date(to).getTime() - now;
  if (left <= 0) return null;
  const p = parts(left);
  const units = [
    { key: "days", value: p.days, label: t("hero.days") },
    { key: "hours", value: p.hours, label: t("hero.hours") },
    { key: "minutes", value: p.minutes, label: t("hero.minutes") },
    { key: "seconds", value: p.seconds, label: t("hero.seconds") },
  ] as const;
  const days = Math.ceil(left / 86_400_000);
  const spoken = days <= 1 ? t("hero.tomorrow") : t("hero.daysToGo", { n: days });

  return (
    <div className={cn("grid gap-2", className)}>
      <p className="font-mono text-[0.68rem] tracking-[0.16em] text-muted-foreground uppercase">
        {t("hero.countdown")}
        <span className="sr-only">: {spoken}</span>
      </p>
      <div className="grid grid-cols-4 gap-1.5" aria-hidden="true">
        {units.map((u) => (
          <div
            key={u.key}
            className="grid justify-items-center rounded-xl bg-foreground px-1 pt-2.5 pb-2 text-background"
          >
            <span className="flex font-display text-2xl leading-none font-bold tabular-nums sm:text-3xl">
              {String(u.value).padStart(2, "0")}
            </span>
            <span className="mt-1 font-mono text-[0.6rem] tracking-[0.14em] text-volt uppercase">
              {u.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
